import { useState, ReactNode } from 'react'
import cx from 'classnames'
import { useResponsiveEnvironment } from '@retail-common/util-hooks'
import { Drawer, Modal } from '@cinch-labs/design-system'
import styles from './dynamic-drawer.module.css'

/**
 * DynamicDrawer is a component that renders a drawer or modal based on the current viewport.
 * It is used to display information in a consistent way across mobile and desktop.
 * @param ariaLabel - The aria-label of the trigger button
 * @param callToActionNode - The 'click' trigger element that opens the slideup/drawer
 * @param content - The content to display inside the drawer/modal
 * @param title - The title of the drawer/modal
 */

export interface DynamicDrawerProps {
  ariaLabel?: string
  callToActionNode: ReactNode
  contentNode: ReactNode
  title: string
  ctaClassName?: string
}

export const DynamicDrawer = ({
  ariaLabel = 'Information drawer',
  callToActionNode,
  contentNode,
  title,
  ctaClassName,
}: DynamicDrawerProps) => {
  const { isMobile } = useResponsiveEnvironment()
  const [isOpen, setIsOpen] = useState(false)

  return (
    <>
      <button
        aria-label={ariaLabel}
        className={cx(styles.triggerButton, ctaClassName)}
        type="button"
        onClick={(event) => {
          event.stopPropagation()
          setIsOpen(true)
        }}
      >
        {callToActionNode}
      </button>

      {isOpen &&
        (isMobile ? (
          <Modal
            title={title}
            titleId="dynamic-drawer"
            className={styles.mobileModal}
            onClose={() => setIsOpen(false)}
          >
            <div className={styles.contentWrapper}>{contentNode}</div>
          </Modal>
        ) : (
          <Drawer
            onClose={() => setIsOpen(false)}
            ariaLabelledById="dynamic-drawer"
            header={title}
          >
            <div className={styles.contentWrapper}>{contentNode}</div>
          </Drawer>
        ))}
    </>
  )
}
